import { CommonModule, CurrencyPipe, DecimalPipe } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, ViewChild, inject } from '@angular/core';
import { FlexLayoutModule } from '@angular/flex-layout';
import { FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatRadioChange, MatRadioModule } from '@angular/material/radio';
import { MatTooltipModule } from '@angular/material/tooltip';
import { LetDirective } from '@ngrx/component';
import { Store, select } from '@ngrx/store';
import {
  AccountObject, Cost, CreditBalance, CreditPool, DealPartyE, DealPaymentTerms, Estimated, ForexData,
  Formula, LocationObject, MATCHED_OFFER_INCOMPLETE, MATCHED_OFFER_PENDING, MATCHED_OFFER_UNSENT,
  MatchedOffer, Measure, Note, ShipmentRate, User
} from '@tradecafe/types/core';
import { DeepReadonly, printPaymentTerms, printFormula, lookupHsCode, tryCalculateAntLiabilityDate } from '@tradecafe/types/utils';
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { compact, get, includes, isArray, isEqual, range, round, sortBy } from 'lodash-es';
import { Observable, ReplaySubject, asapScheduler, combineLatest, from, of, merge } from 'rxjs';
import { catchError, distinctUntilChanged, map, observeOn, shareReplay, skip, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import { GenericOption, loadAccounts, selectAccountEntities } from 'src/app/store/accounts';
import { loadCountries, selectAllCountries } from 'src/app/store/countries';
import { loadCurrencies, selectAllCurrencies } from 'src/app/store/currencies';
import { loadItemTypes, selectAllItemTypes } from 'src/app/store/item-types';
import { loadLocations, selectAllLocations, selectLocationEntities } from 'src/app/store/locations';
import { loadMeasures, selectAllMeasures, selectMeasureEntities } from 'src/app/store/measures';
import { loadPackageTypes, selectAllPackageTypes } from 'src/app/store/package-types';
import { loadPricingTerms, selectAllPricingTerms, selectPricingTermEntities } from 'src/app/store/pricing-terms';
import { loadUnlocodes, selectAllUnlocodes } from 'src/app/store/unlocodes';
import { loadUsers, selectCoordinatorsOptions, selectTradersOptions, selectUserEntities } from 'src/app/store/users';
import { loadWeightTypes, selectAllWeightTypes } from 'src/app/store/weight-types';
import { loadWrappingTypes, selectAllWrappingTypes } from 'src/app/store/wrapping-types';
import { AddressFieldModule } from 'src/components/address-field/address-field.module';
import { AddressPickerOptions } from 'src/components/address-picker/address-picker.component';
import { CostsListModule } from 'src/components/costs-list/costs-list.module';
import { CreditBalanceComponent } from 'src/components/credit-balance/credit-balance.component';
import { EpochFieldModule } from 'src/components/epoch/epoch-field/epoch-field.module';
import { EpochRangeFieldModule } from 'src/components/epoch/epoch-range-field/epoch-range-field.module';
import { ProductFieldModule } from 'src/components/product-field/product-field.module';
import { ReactiveAsteriskModule } from 'src/components/reactive-asterisk/reactive-asterisk.module';
import { SelectSearchComponent } from 'src/components/select-search/select-search.component';
import { SelectSearchModule } from 'src/components/select-search/select-search.module';
import { ValidationWarningComponent } from 'src/components/validation-warning/validation-warning.component';
import { environment } from 'src/environments/environment';
import { MeasurePipe } from 'src/filters/measure.pipe';
import { CostsFormGroup } from 'src/pages/admin/trading/deal-form/deal-form-page/deal-form.schema';
import { getPartyUsersMO } from 'src/services/data/accounts.service';
import { CreditPoolService } from 'src/services/data/credit-pool.service';
import { MeasuresService } from 'src/services/data/measures.service';
import { SPECIAL_INSTRUCTIONS } from 'src/services/data/notes.service';
import { dayjs } from 'src/services/dayjs';
import { PipesModule } from 'src/shared/pipes/pipes.module';
import { disableIf } from 'src/shared/utils/disable-if';
import { replayForm } from 'src/shared/utils/replay-form';
import { selectOptions } from 'src/shared/utils/select-options';
import { waitNotEmpty } from 'src/shared/utils/wait-not-empty';
import { setupItemSpecPackageAutoPopulation } from 'src/services/data/item-spec-package.utils';
import { NoteFormService } from '../../../../../components/notes/note-form/note-form.service';
import { FinanceService } from '../finance.service';
import { ToasterService } from 'src/shared/toaster/toaster.service';
import { MatchedOfferFormGroup, MatchedOfferFormService, MatchedOfferFormValue, stampBuyerIncotermOnFormula } from '../matched-offer-form.service';
import { loadProducts, selectProductEntities } from 'src/app/store/products';
import { MatchedOfferFormulaDialogService } from '../matched-offer-formula-dialog';
import { DealDocumentsService } from 'src/pages/admin/trading/deals/deal-documents/deal-documents.service';
import { isUsdaProposalOnDealEnabled } from 'src/components/deals-list/formula-pricing';
import { UsdaProposalLookupService, UsdaProposalPanelComponent, type UsdaDealLineLookup } from 'src/components/usda-proposal-panel';
import { isUsdaPriceSource } from 'src/shared/formula-form';

@Component({
    selector: 'tc-matched-offer-details',
    templateUrl: './matched-offer-details.component.html',
    styleUrls: ['./matched-offer-details.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        MatAutocompleteModule,
        MatButtonModule,
        MatCheckboxModule,
        MatIconModule,
        MatFormFieldModule,
        MatInputModule,
        MatOptionModule,
        MatRadioModule,
        MatTooltipModule,
        ReactiveAsteriskModule,
        ReactiveFormsModule,
        FormsModule,
        SelectSearchModule,
        FlexLayoutModule,
        EpochRangeFieldModule,
        CreditBalanceComponent,
        ProductFieldModule,
        AddressFieldModule,
        PipesModule,
        CostsListModule,
        EpochFieldModule,
        LetDirective,
        ValidationWarningComponent,
        UsdaProposalPanelComponent,
    ]
})
export class MatchedOfferDetailsComponent extends OnDestroyMixin implements OnInit {
  private readonly store = inject(Store)
  private readonly Measures = inject(MeasuresService)
  private readonly CreditPool = inject(CreditPoolService)
  private readonly Finance = inject(FinanceService)
  private readonly NoteForm = inject(NoteFormService)
  private readonly measure = inject(MeasurePipe)
  private readonly currency = inject(CurrencyPipe)
  private readonly MatchedOfferForm = inject(MatchedOfferFormService)
  private readonly decimalPipe = inject(DecimalPipe)
  private readonly MatchedOfferFormulaDialog = inject(MatchedOfferFormulaDialogService)
  private readonly DealDocuments = inject(DealDocumentsService)
  private readonly cd = inject(ChangeDetectorRef)
  private readonly UsdaProposalLookup = inject(UsdaProposalLookupService)

  constructor() {
    super();
    this.enableRouteService = environment.enableRouteService ?? false;
    this.enableFreightRatePaths = environment.enableFreightRatePaths ?? false;
  }

  private readonly toaster = inject(ToasterService);

  @Input() matchedOffer: DeepReadonly<MatchedOffer>
  @Input() isAutomatedOffer: boolean
  @Input() moForm: MatchedOfferFormGroup
  @Input() costsForm: CostsFormGroup
  @Input() fxRates: ForexData
  @Input() shipmentRates$: Observable<DeepReadonly<Dictionary<ShipmentRate>>>
  @Input() currentUser?: User;
  @Output() updateCost = new EventEmitter<DeepReadonly<Partial<Cost>>>()
  @Output() removeCost = new EventEmitter<DeepReadonly<Partial<Cost>>>()

  protected readonly enableRouteService: boolean = false;
  protected readonly enableFreightRatePaths: boolean = false;

  @ViewChild('originPort') pickupPortElement: SelectSearchComponent<boolean>;
  @ViewChild('destPort') destPortElement: SelectSearchComponent<boolean>;

  protected buyerFormulaString: string
  protected supplierFormulaString: string
  /** WA-22298: the buyer formula is retunable, but only while the MO is still unsent */
  protected buyerFormulaEditable = false
  /** WA-22777: the deal line of an MO that is on a deal (`not-found` = no deal); undefined = no USDA buyer formula / flag off */
  protected usdaBuyerLine$: Observable<UsdaDealLineLookup> | undefined
  protected readonly USDA_DEAL_LOAD_FAILED = 'Couldn\'t load the deal\'s USDA proposal right now.'
  protected supplierName$: Observable<string>
  protected buyerName$: Observable<string>
  protected buyerCurrency$: Observable<string>
  protected docsCountryIssues$: Observable<string>
  protected hsCode$: Observable<string>
  private buyer$: Observable<DeepReadonly<AccountObject>>
  protected supplierAvgDaysToDue$: Observable<number>
  protected buyerAvgDaysToDue$: Observable<number>
  protected readonly balance$ = new ReplaySubject<CreditBalance>(1)
  protected readonly supplierCreditPool$ = new ReplaySubject<CreditPool>(1)
  protected readonly buyerCreditPool$ = new ReplaySubject<CreditPool>(1)
  private readonly supplierPaymentTerms$ = new ReplaySubject<DeepReadonly<DealPaymentTerms>>(1)
  private readonly buyerPaymentTerms$ = new ReplaySubject<DeepReadonly<DealPaymentTerms>>(1)
  protected readonly supplierPaymentTermsStr$ = this.supplierPaymentTerms$.pipe(map(pt => printPaymentTerms(pt)))
  protected readonly buyerPaymentTermsStr$ = this.buyerPaymentTerms$.pipe(map(pt => printPaymentTerms(pt)))
  protected hasSupplierInstructions$: Observable<DeepReadonly<Note>>
  protected hasBuyerInstructions$: Observable<DeepReadonly<Note>>
  protected supplierIndWeight$: Observable<string>
  protected supplierIndPrice$: Observable<string>
  protected buyerIndWeight$: Observable<string>
  protected buyerIndPrice$: Observable<string>
  // totals
  private readonly totals$ = new ReplaySubject<Estimated>(1)
  protected readonly buyTotalPrice$ = this.totals$.pipe(map(t => t.buy_price))
  protected readonly buyTotalPriceCad$ = this.totals$.pipe(map(t => t.buy_price_cad))
  protected readonly sellTotalPrice$ = this.totals$.pipe(map(t => t.sell_price))
  protected readonly sellTotalPriceCad$ = this.totals$.pipe(map(t => t.sell_price_cad))
  protected readonly rawCost$ = this.totals$.pipe(map(t => t.raw_cost ))
  protected readonly targetCost$ = this.totals$.pipe(map(t => t.target_cost ))
  public readonly revenueCad$ = this.totals$.pipe(map(t => t.revenue ))
  public readonly marginCad$ = this.totals$.pipe(map(t => t.margin ))
  public readonly margin$ = this.totals$.pipe(map(t =>
    t.margin_p ? `${this.decimalPipe.transform(t.margin_p * 100, '1.2-2')}%` : '—'))

  // ref data
  protected readonly TODAY = dayjs().utc().startOf('day').unix()
  protected readonly weights = range(5000, 26501, 500)
  protected readonly countries$ = this.store.pipe(select(selectAllCountries), waitNotEmpty(), map(c => sortBy(c, 'name')))
  protected readonly currencies$ = this.store.pipe(select(selectAllCurrencies), waitNotEmpty())
  protected readonly itemTypes$ = this.store.pipe(select(selectAllItemTypes), waitNotEmpty())
  protected readonly locations$ = this.store.pipe(select(selectAllLocations), waitNotEmpty())
  protected readonly unlocodes$ = this.store.pipe(select(selectAllUnlocodes), waitNotEmpty())
  protected readonly measures$ = this.store.pipe(select(selectAllMeasures), waitNotEmpty())
  protected readonly packageTypes$ = this.store.pipe(select(selectAllPackageTypes), waitNotEmpty())
  protected readonly pricingTerms$ = this.store.pipe(select(selectAllPricingTerms), waitNotEmpty())
  protected readonly weightTypes$ = this.store.pipe(select(selectAllWeightTypes), waitNotEmpty())
  protected readonly wrappings$ = this.store.pipe(select(selectAllWrappingTypes), waitNotEmpty())
  protected readonly accounts$ = this.store.pipe(select(selectAccountEntities), waitNotEmpty())
  protected readonly products$ = this.store.pipe(select(selectProductEntities), waitNotEmpty())

  // address picker options
  protected establishmentAddressOptions$: Observable<DeepReadonly<AddressPickerOptions>>
  protected invoiceAddressOptions$: Observable<DeepReadonly<AddressPickerOptions>>

  // user options
  private users$ = this.store.pipe(select(selectUserEntities), waitNotEmpty())
  protected buyerTraders$: Observable<GenericOption[]>
  protected buyerUsers$: Observable<DeepReadonly<User[]>>
  protected coordinators$: Observable<GenericOption[]>
  protected supplierTraders$: Observable<GenericOption[]>
  protected supplierUsers$: Observable<DeepReadonly<User[]>>

  ngOnInit() {
    // initial render shows the stored margin (the Default margin override seed);
    // later edits recalculate it (see calculateOffer)
    this.calculateOffer({ keepStoredMargin: true })
    this.store.dispatch(loadCountries())
    this.store.dispatch(loadCurrencies({}))
    this.store.dispatch(loadItemTypes())
    this.store.dispatch(loadLocations({}))
    this.store.dispatch(loadMeasures())
    this.store.dispatch(loadPackageTypes())
    this.store.dispatch(loadPricingTerms({}))
    this.store.dispatch(loadWeightTypes())
    this.store.dispatch(loadWrappingTypes())
    this.store.dispatch(loadAccounts({}))
    this.store.dispatch(loadUsers({}))
    this.store.dispatch(loadUnlocodes())
    this.store.dispatch(loadProducts())


    // address picker options
    this.establishmentAddressOptions$ = replayForm(this.moForm).pipe(map(mo => ({
      accountId: this.matchedOffer.offer.account,
      addresses: mo.establishments,
      title: 'Select Establishment',
      showEstablishment: true,
      allowMultiple: true,
      limit: 3,
    })))
    this.invoiceAddressOptions$ = replayForm(this.moForm).pipe(map(mo => ({
      title: 'Select Invoice Address',
      accountId: this.matchedOffer.bid.account,
      address: mo.invoiceAddress,
    })))

    // user options
    this.coordinators$ = selectOptions(this.store, selectCoordinatorsOptions, this.moForm.controls.logisticsUserId)
    this.buyerTraders$ = selectOptions(this.store, selectTradersOptions, this.moForm.controls.buyerTraderId)
    this.supplierTraders$ = selectOptions(this.store, selectTradersOptions, this.moForm.controls.supplierTraderId)
    this.supplierUsers$ = this.users$.pipe(map(users => getPartyUsersMO(users, this.matchedOffer.offer.account).filter(user => !user.archived || this.moForm.controls.supplierUserIds.value?.includes(user.user_id))))
    this.buyerUsers$ = this.users$.pipe(map(users => getPartyUsersMO(users, this.matchedOffer.bid.account).filter(user => !user.archived || this.moForm.controls.buyerUserIds.value?.includes(user.user_id))))

    const supplierId = this.matchedOffer.offer.account
    const buyerId = this.matchedOffer.bid.account
    const supplier$ = this.accounts$.pipe(map(accounts => accounts[supplierId]))
    this.buyer$ = this.accounts$.pipe(map(accounts => accounts[buyerId]))
    this.supplierName$ = supplier$.pipe(map(acc => acc.name))
    this.buyerName$ = this.buyer$.pipe(map(acc => acc.name))
    this.supplierAvgDaysToDue$ = supplier$.pipe(map(acc => acc.attributes.credit_info?.avg_days_to_due_date))
    this.buyerAvgDaysToDue$ = this.buyer$.pipe(map(acc => acc.attributes.credit_info?.avg_days_to_due_date))
    this.buyerCurrency$ = replayForm(this.moForm.controls.buyerCurrencyCode)
    this.hasBuyerInstructions$ = replayForm(this.moForm).pipe(map(mo => this.hasBuyerInstructions(mo)))
    this.hasSupplierInstructions$ = replayForm(this.moForm).pipe(map(mo => this.hasSupplierInstructions(mo)))
    this.docsCountryIssues$ = this.DealDocuments.docsCountryIssues$(
      combineLatest([replayForm(this.moForm), this.products$]).pipe(map(([mo, products]) => ({
        buyer_id: String(buyerId),
        country_code: mo.docsCountryCode,
        supplier_formula: !!this.matchedOffer.offer.formula,
        buyer_formula: !!mo.buyerFormula,
        product_category_id: compact([products[mo.productId]?.category_id]),
        buyer_incoterm: mo.buyerIncotermId,
        supplier_incoterm: mo.supplierIncotermId,
      }))))

    this.hsCode$ = combineLatest([
      this.buyer$,
      replayForm(this.moForm.controls.productId),
      replayForm(this.moForm.controls.itemTypeId),
      this.products$,
    ]).pipe(map(([buyer, product_id, item_type_id, products]) => {
      return lookupHsCode(buyer, products[product_id], item_type_id) || ''
    }))

    this.supplierIndWeight$ = replayForm(this.moForm).pipe(map(({ supplierEstWeight, supplierMeasureId }) =>
        supplierEstWeight || supplierEstWeight === 0
          ? this.measure.transform(supplierEstWeight, supplierMeasureId)
          : ''))
    this.buyerIndWeight$ = replayForm(this.moForm).pipe(map(({ buyerEstWeight, buyerMeasureId }) =>
        buyerEstWeight || buyerEstWeight === 0
          ? this.measure.transform(buyerEstWeight, buyerMeasureId)
          : ''))
    this.supplierIndPrice$ = replayForm(this.moForm).pipe(map(({ supplierEstPrice, supplierCurrencyCode, supplierMeasureId }) =>
      supplierEstPrice && supplierCurrencyCode && supplierMeasureId
        ? this.measure.transform(
            this.currency.transform(supplierEstPrice, supplierCurrencyCode, 'symbol-narrow', '1.4'/* , 'no_TH' */),
            supplierMeasureId)
        : ''))
    this.buyerIndPrice$ = replayForm(this.moForm).pipe(map(({ buyerEstPrice, buyerCurrencyCode, buyerMeasureId }) =>
      buyerEstPrice && buyerCurrencyCode && buyerMeasureId
        ? this.measure.transform(
            this.currency.transform(buyerEstPrice, buyerCurrencyCode, 'symbol-narrow', '1.4'/* , 'no_TH' */),
            buyerMeasureId)
        : ''))

    combineLatest([
      combineLatest([this.supplierCreditPool$, replayForm(this.moForm.controls.supplierTraderId)]).pipe(
        switchMap(([creditPool, traderId]) => from(this.CreditPool.readPaymentTerms(creditPool, DealPartyE.supplier, traderId))),
        tap(paymentTerms => this.supplierPaymentTerms$.next(paymentTerms))),
      combineLatest([this.buyerCreditPool$, replayForm(this.moForm.controls.buyerTraderId)]).pipe(
        switchMap(([creditPool, traderId]) => from(this.CreditPool.readPaymentTerms(creditPool, DealPartyE.buyer, traderId))),
        tap(paymentTerms => this.buyerPaymentTerms$.next(paymentTerms))),
    ]).pipe(untilComponentDestroyed(this)).subscribe()

    combineLatest([this.supplierPaymentTerms$, this.buyerPaymentTerms$, this.accounts$, this.buyer$]).pipe(take(1))
    .subscribe(() => {
      // Trust the matched offer's persisted finance dates (supplier liability /
      // collection) instead of re-deriving them on open. The auctioneer already
      // computed them; recomputing here overwrote them and zeroed the finance
      // term (Interest), inflating the margin. User-driven changes to shipment /
      // delivery dates or payment terms still recompute via keepTermDateInSync().
      this.calculateOffer()
    })

    disableIf(this.moForm.controls.originIsPort, !(this.enableRouteService && this.enableFreightRatePaths));
    disableIf(this.moForm.controls.destIsPort, !(this.enableRouteService && this.enableFreightRatePaths));

    this.setupItemTypeAutoPopulation()

    // fetch credit pools & balance
    this.CreditPool.getFor(supplierId).then(supplierCreditPool => this.supplierCreditPool$.next(supplierCreditPool))
    this.CreditPool.getFor(buyerId).then(buyerCreditPool => this.buyerCreditPool$.next(buyerCreditPool))
    this.CreditPool.getBalance(buyerId).pipe(
      catchError((err: any) => {
        console.error('Unable to fetch buyer balance', err)
        this.toaster.error('Unable to fetch buyer balance', err?.error?.error?.error?.message || err)
        return of(undefined)
      }),
    ).subscribe(balance => { if (balance) this.balance$.next(balance) })

    if (this.matchedOffer?.offer.formula || this.matchedOffer?.bid.formula) {
      // WA-22298: once the MO has been sent the buyer has been quoted, and the offer service
      // freezes desired_flat_price - so the formula stays read-only from that point on.
      this.buyerFormulaEditable =
        !!this.moForm.controls.buyerFormula.value && isMatchedOfferUnsent(this.matchedOffer)

      // WA-22777: an MO gets a deal_id when it is accepted; the deal's buyer line is a new bid (see UsdaProposalLookupService)
      if (isUsdaProposalOnDealEnabled() && isUsdaPriceSource(this.moForm.controls.buyerFormula.value?.price_source)) {
        this.usdaBuyerLine$ = this.matchedOffer.deal_id
          ? this.UsdaProposalLookup.find(this.matchedOffer.deal_id, 'buyer', {
            bidId: this.matchedOffer.bid?.bid_id,
            offerId: this.matchedOffer.supplier_offer_id,
          }).pipe(shareReplay(1))
          : of<UsdaDealLineLookup>({ status: 'not-found' })
      }

      merge(
        this.moForm.controls.buyerIncotermId.valueChanges,
        this.moForm.controls.buyerIncotermLocationId.valueChanges
      ).pipe(untilComponentDestroyed(this)).subscribe(() => {
        this.updateBidFormula()
        this.updateFormulaStrings()
      });

      this.updateFormulaStrings()
    }
  }

  /**
   * Keep the buyer formula lines in step with the Buyer Incoterm fields, editable formula or not
   * (WA-22644). The fields are the MO's incoterm of record; the formula dialog's own incoterm and
   * location edits are pushed back into them in `showBuyerFormulaDialog`, so nothing is lost.
   * Writes through the form control so the value reaches `readMatchedOfferForm`.
   */
  private updateBidFormula(): void {
    const formula = this.moForm.controls.buyerFormula.value
    if (!formula?.lines?.length) return

    this.moForm.controls.buyerFormula.setValue(stampBuyerIncotermOnFormula(
      formula,
      this.moForm.controls.buyerIncotermId.value,
      this.moForm.controls.buyerIncotermLocationId.value,
    ))
  }

  /**
   * WA-22644: an incoterm or location picked in the formula dialog becomes the MO's own, so the
   * Buyer Incoterm fields, `bid.incoterm` and the formula lines all agree.
   */
  private applyFormulaIncotermToBuyerFields(formula: DeepReadonly<Formula>): void {
    const line = formula?.lines?.[0]
    if (!line) return

    const { buyerIncotermId, buyerIncotermLocationId } = this.moForm.controls
    if (line.incoterm && line.incoterm !== buyerIncotermId.value) {
      buyerIncotermId.setValue(line.incoterm)
      buyerIncotermId.markAsDirty()
    }
    if (line.location && line.location !== buyerIncotermLocationId.value) {
      buyerIncotermLocationId.setValue(line.location)
      buyerIncotermLocationId.markAsDirty()
    }
  }

  /**
   * WA-22298: retune the buyer side of a formula MO - days, before date, differential,
   * incoterm, incoterm location and conditions. A changed differential moves the flat price,
   * which feeds the margin.
   */
  protected async showBuyerFormulaDialog() {
    if (!this.buyerFormulaEditable) return

    const formula = this.moForm.controls.buyerFormula.value
    if (!formula) return

    // without the index there is nothing to add the differential to, and saving would let the
    // offer service recompute the differential off the unchanged flat price - silently undoing the edit
    const indexPrice = this.buyerIndexPrice()
    if (indexPrice === null) {
      this.toaster.error('Unable to edit the buyer formula', 'The buyer index price could not be determined for this offer')
      return
    }

    const products = await this.store.pipe(select(selectProductEntities), waitNotEmpty(), take(1)).toPromise()
    const productName = formula.product_name || products[this.moForm.controls.productId.value]?.name || ''

    const usdaLine = this.usdaBuyerLine$ ? await this.usdaBuyerLine$.pipe(take(1)).toPromise() : undefined
    const result = await this.MatchedOfferFormulaDialog.show({
      dealLine: usdaLine?.status === 'found' ? usdaLine.line : null,
      formula,
      currency: this.moForm.controls.buyerCurrencyCode.value,
      measureId: this.moForm.controls.buyerMeasureId.value,
      defaultIncoterm: this.moForm.controls.buyerIncotermId.value,
      defaultLocation: this.moForm.controls.buyerIncotermLocationId.value,
      buyerId: Number(this.matchedOffer.bid.account),
      productName,
      indexPrice,
    })

    // undefined means the dialog was cancelled
    if (result === undefined) return

    this.moForm.controls.buyerFormula.setValue(result.formula)
    this.moForm.controls.buyerFormula.markAsDirty()
    if (result.formula) {
      this.applyFormulaIncotermToBuyerFields(result.formula)
      this.updateBidFormula() // the fields may not have changed, and later lines still need stamping
    } else {
      // WA-22777: "Use proposed price" switched the buyer side to flat; saving sends `formula: null`
      this.buyerFormulaString = null
      this.buyerFormulaEditable = false
    }

    if (result.flatPrice !== null) {
      this.moForm.controls.buyerEstPrice.setValue(round(result.flatPrice, 4))
      this.moForm.controls.buyerEstPrice.markAsDirty()
    }

    this.updateFormulaStrings()
    this.onPriceChange() // the differential moved the flat price -> recalculate margin
  }

  /**
   * The buyer index the differential is quoted against. Stamped onto the MO as `bid.buyerIndex`
   * when it was generated; MOs predating that fall back to flat price minus the stored
   * differential, which is the same derivation the supplier offer overlay uses.
   */
  private buyerIndexPrice(): number | null {
    const stored = get(this.matchedOffer, 'bid.buyerIndex') as number
    if (typeof stored === 'number' && !isNaN(stored)) return stored

    const differential = this.moForm.controls.buyerFormula.value?.lines?.[0]?.price
    const flatPrice = this.moForm.controls.buyerEstPrice.value
    if (typeof flatPrice !== 'number' || typeof differential !== 'number') return null

    return round(flatPrice - differential, 4)
  }

  private updateFormulaStrings(): void {
    combineLatest([
      this.store.pipe(select(selectLocationEntities), waitNotEmpty()),
      this.store.pipe(select(selectMeasureEntities), waitNotEmpty()),
      this.store.pipe(select(selectPricingTermEntities), waitNotEmpty()),
    ]).pipe(take(1)).subscribe(([locations, measures, pricingTerms]): void => {
      const buyerFormula = this.moForm.controls.buyerFormula.value
      if (buyerFormula) {
        this.buyerFormulaString = printFormula(
          buyerFormula,
          this.moForm.controls.buyerCurrencyCode.value,
          this.moForm.controls.buyerMeasureId.value,
          buyerFormula.lines[0].incoterm,
          {locations, measures, pricingTerms})
      }

      if (this.matchedOffer.offer.formula) {
        this.supplierFormulaString = printFormula(
          this.matchedOffer.offer.formula,
          this.matchedOffer.offer.currency,
          this.matchedOffer.offer.weight.unit,
          this.matchedOffer.offer.incoterm,
          {locations, measures, pricingTerms})
      }

      this.cd.detectChanges()
    })
  }

  protected async onOriginCountryChange() {
    await this.refreshSecondaryCosts()
    await this.calculateOffer()
  }

  protected async onDocsCountryChange() {
    // Proforma document is mandatory for MOs with documents country = Colombia or South Korea
    const PROFORMA_REQUIRED_COUNTRIES = ['CO', 'KOR', 'PRK', 'KR']
    if (PROFORMA_REQUIRED_COUNTRIES.includes(this.moForm.controls.docsCountryCode.value)) {
      this.moForm.controls.proformaNeeded.setValue(true)
    }
    await this.refreshSecondaryCosts()
    await this.calculateOffer()
  }

  protected async onOriginLocationChange(locationId: DeepReadonly<LocationObject>) {
    const ctrl = this.moForm.controls.supplierIncotermLocationId
    ctrl.setValue(locationId.location_id)
    ctrl.markAsDirty()
    await this.refreshSecondaryCosts()
    await this.calculateOffer()
  }

  protected onOriginTypeChange(change: MatRadioChange) {
    const ctrl = this.moForm.controls.originPortId;
    if (change.value) {
      ctrl.addValidators(Validators.required);
    } else {
      ctrl.clearValidators();
    }
    ctrl.updateValueAndValidity();
    this.pickupPortElement.checkIfRequired();
  }

  protected onDestTypeChange(change: MatRadioChange) {
    const ctrl = this.moForm.controls.destPortId;
    if (change.value) {
      ctrl.addValidators(Validators.required);
    } else {
      ctrl.clearValidators();
    }
    ctrl.updateValueAndValidity();
    this.destPortElement.checkIfRequired();
  }

  protected async onDestLocationChange(locationId: DeepReadonly<LocationObject>) {
    const ctrl = this.moForm.controls.buyerIncotermLocationId
    ctrl.setValue(locationId.location_id)
    ctrl.markAsDirty()
    await this.refreshSecondaryCosts()
    await this.calculateOffer()
  }

  protected async onProductChange() {
    await this.refreshSecondaryCosts()
    await this.calculateOffer()
  }

  private setupItemTypeAutoPopulation() {
    setupItemSpecPackageAutoPopulation(
      this.moForm.controls.itemTypeId,
      this.moForm.controls.packageId,
      this
    )
  }

  protected onSupplierMeasureChange(nextMeasure: Measure) {
    const { supplierMeasureId: prevMeasure, supplierEstWeight: prevWeight } = this.moForm.getRawValue()
    const nextWeight = round(this.Measures.convert(prevWeight, prevMeasure, nextMeasure), 2)
    this.moForm.patchValue({ supplierEstWeight: nextWeight || prevWeight })
    this.onSupplierWeightChange()
  }

  protected onBuyerMeasureChange(nextMeasure: Measure) {
    const { buyerMeasureId: prevMeasure, buyerEstWeight: prevWeight } = this.moForm.getRawValue()
    const nextWeight = round(this.Measures.convert(prevWeight, prevMeasure, nextMeasure), 2)
    this.moForm.patchValue({ buyerEstWeight: nextWeight || prevWeight })
    this.onBuyerWeightChange()
  }

  protected onSupplierWeightChange() {
    const { supplierEstWeight, supplierMeasureId, buyerMeasureId } = this.moForm.getRawValue()
    const buyerEstWeight = round(this.Measures.convert(supplierEstWeight, supplierMeasureId, buyerMeasureId), 2)
    this.moForm.controls.buyerEstWeight.setValue(buyerEstWeight)
    this.moForm.controls.buyerEstWeight.markAsDirty()
    this.calculateOffer({ keepStoredMargin: false }) // price/cost edit -> recalculate margin
  }

  protected onBuyerWeightChange() {
    this.calculateOffer({ keepStoredMargin: false }) // price/cost edit -> recalculate margin
  }

  protected onPriceChange() {
    this.calculateOffer({ keepStoredMargin: false }) // offer-price edit -> recalculate margin
  }

  protected onCurrencyChange() {
    this.calculateOffer({ keepStoredMargin: false }) // price/cost edit -> recalculate margin
  }

  keepTermDateInSync() {
    return this.accounts$.pipe(take(1), switchMap(accounts => {
      const calculateTerms = (
        changedField: /* keyof SegmentFormValue |  */keyof MatchedOfferFormValue,
        supplierPaymentTerms: DealPaymentTerms,
        buyerPaymentTerms: DealPaymentTerms,
      ) => {
        const supplier = accounts[this.matchedOffer.offer.account]
        const buyer = accounts[this.matchedOffer.bid.account]
        const changed = this.calculateTermDate({ changedField, supplierPaymentTerms, buyerPaymentTerms, supplier, buyer })
        return changed ? this.calculateOffer() : of(false)
      }
      const calculateCollectionDate = (buyerTermDate: number, buyerPaymentTerms: DealPaymentTerms) => {
        if (!buyerTermDate && !buyerPaymentTerms) return
        const buyer = accounts[this.matchedOffer.bid.account]
        this.moForm.patchValue({
          collectionDate: tryCalculateAntLiabilityDate(buyerTermDate, buyerPaymentTerms, buyer),
        })
      }

      return combineLatest([
        replayForm(this.moForm.controls.shipmentDatesTo).pipe(
          distinctUntilChanged(isEqual), skip(1), observeOn(asapScheduler),
          withLatestFrom(this.supplierPaymentTerms$), withLatestFrom(this.buyerPaymentTerms$),
          switchMap(([[, spt], bpt]) => calculateTerms('shipmentDatesTo', spt, bpt))),
        replayForm(this.moForm.controls.deliveryDatesTo).pipe(
          distinctUntilChanged(isEqual), skip(1), observeOn(asapScheduler),
          withLatestFrom(this.supplierPaymentTerms$), withLatestFrom(this.buyerPaymentTerms$),
          switchMap(([[, spt], bpt]) => calculateTerms('deliveryDatesTo', spt, bpt))),
        // this.supplierPaymentTerms$.pipe(
        //   distinctUntilChanged(isEqual), skip(1), observeOn(asapScheduler),
        //   withLatestFrom(this.buyerPaymentTerms$), switchMap(([spt, bpt]) => calculateTerms('supplierPaymentTerms', spt, bpt))),
        // this.buyerPaymentTerms$.pipe(
        //   distinctUntilChanged(isEqual), skip(1), observeOn(asapScheduler),
        //   withLatestFrom(this.supplierPaymentTerms$), switchMap(([bpt, spt]) => calculateTerms('buyerPaymentTerms', spt, bpt))),
        replayForm(this.moForm.controls.buyerTermDate).pipe(
          distinctUntilChanged(isEqual), skip(1), observeOn(asapScheduler),
          withLatestFrom(this.buyerPaymentTerms$), tap(([buyerTermDate, bpt]) => calculateCollectionDate(buyerTermDate, bpt))),
        replayForm(this.moForm.controls.supplierLiabilityDate).pipe(
          distinctUntilChanged(isEqual), skip(1), observeOn(asapScheduler),
          switchMap(() => this.calculateOffer())),
        replayForm(this.moForm.controls.collectionDate).pipe(
          distinctUntilChanged(isEqual), skip(1), observeOn(asapScheduler),
          switchMap(() => this.calculateOffer())),
      ])
    }))
  }

  protected onShipmentDatesChange() {
    combineLatest([this.accounts$, this.buyer$, this.buyerPaymentTerms$, this.supplierPaymentTerms$]).pipe(take(1))
    .subscribe(([accounts, buyer, buyerPaymentTerms, supplierPaymentTerms]) => {
      const supplier = accounts[this.matchedOffer.offer.account]
      if (this.calculateTermDate({ changedField: 'shipmentDate', supplier, buyer, buyerPaymentTerms, supplierPaymentTerms })) {
        this.calculateOffer()
      }
    })
  }

  protected onDeliveryDatesChange() {
    combineLatest([this.accounts$, this.buyer$, this.buyerPaymentTerms$, this.supplierPaymentTerms$]).pipe(take(1))
    .subscribe(([accounts, buyer, buyerPaymentTerms, supplierPaymentTerms]) => {
      const supplier = accounts[this.matchedOffer.offer.account]
      if (this.calculateTermDate({ changedField: 'deliveryDate', supplier, buyer, buyerPaymentTerms, supplierPaymentTerms })) {
        this.calculateOffer()
      }
    })
  }

  protected onSupplierLiabilityChange() {
    this.calculateOffer()
  }

  protected onBuyerTermDateChange(termDate: number) {
    combineLatest([this.buyer$, this.buyerPaymentTerms$]).pipe(take(1)).subscribe(([buyer, buyerPaymentTerms]) => {
      this.moForm.patchValue({
        buyerTermDate: termDate || undefined,
        collectionDate: tryCalculateAntLiabilityDate(termDate, buyerPaymentTerms, buyer),
      })
      this.calculateOffer()
    })
  }

  protected onCollectionDateChange() {
    this.calculateOffer()
  }

  private async refreshSecondaryCosts() {
    await this.MatchedOfferForm.refreshSecondaryCosts(
      this.matchedOffer.offer.account,
      this.matchedOffer.bid.account,
      this.moForm,
      this.costsForm,
      this.currentUser,
    )
  }


  /**
   * @param keepStoredMargin Defaults to TRUE: the margin shown is the matched
   * offer's stored value (the "Default margin override" seed), kept consistent
   * with the MO table list. Only an actual offer-price/weight/currency edit
   * passes FALSE to recalculate the margin from the edited prices.
   */
  private async calculateOffer({ keepStoredMargin = true }: { keepStoredMargin?: boolean } = {}) {
    const data = await this.Finance.calculateMatchedOfferForm(this.matchedOffer, this.moForm, this.costsForm, { keepStoredMargin })
    this.totals$.next(data.deal.attributes.estimated)
  }

  private calculateTermDate({
    changedField,
    supplierPaymentTerms,
    buyerPaymentTerms,
    supplier,
    buyer,
  }: {
    changedField?: string
    supplierPaymentTerms: DeepReadonly<DealPaymentTerms>
    buyerPaymentTerms: DeepReadonly<DealPaymentTerms>
    supplier: DeepReadonly<AccountObject>
    buyer: DeepReadonly<AccountObject>
  }) {
    let changed = false
    const mo = this.moForm.getRawValue()
    const created = this.matchedOffer.created

    if (!changedField || this.Finance.isFinanceDate(changedField, supplierPaymentTerms, true)) {
      const { termDate, balanceDate, liabilityDate, liabilityDateIsWeighted } = this.Finance.getTermDates({
        created,
        shipmentDate: mo.shipmentDatesTo,
        deliveryDate: mo.deliveryDatesTo,
        paymentTerms: supplierPaymentTerms,
        account: supplier,
        supplier: true,
      })

      this.moForm.patchValue({
        supplierTermDate: termDate || undefined,
        supplierBalanceLiabilityDate: balanceDate,
        supplierLiabilityDate: liabilityDate,
        supplierLiabilityDateIsWeighted: liabilityDateIsWeighted,
      })

      changed = true
    }

    if (!changedField || this.Finance.isFinanceDate(changedField, buyerPaymentTerms)) {
           const { termDate, liabilityDate } = this.Finance.getTermDates({
        created,
        shipmentDate: mo.shipmentDatesTo,
        deliveryDate: mo.deliveryDatesTo,
        paymentTerms: buyerPaymentTerms,
        account: buyer,
      });

      this.moForm.patchValue({
        buyerTermDate: termDate || undefined,
        collectionDate: liabilityDate,
      })

      changed = true
    }

    return changed
  }


  private hasSupplierInstructions(mo: MatchedOfferFormValue) {
    return mo.supplierInstructions?.find(note => {
      const companies = isArray(note.attributes?.company) ? note.attributes?.company : [note.attributes?.company]
      return note.attributes &&
        includes(companies.map(parseFloat), this.matchedOffer.offer.account) &&
        note.attributes.category === SPECIAL_INSTRUCTIONS
    })
  }

  private hasBuyerInstructions(mo: MatchedOfferFormValue) {
    return mo.buyerInstructions?.find(note => {
      const companies = isArray(note.attributes?.company) ? note.attributes?.company : [note.attributes?.company]
      return note.attributes &&
        includes(companies.map(parseFloat), this.matchedOffer.bid.account) &&
        note.attributes.category === SPECIAL_INSTRUCTIONS
    })
  }

  protected async addSupplierSpecialInstructions() {
    const existing = this.hasSupplierInstructions(this.moForm.getRawValue())
    const title = existing ? 'Update Supplier Special Instructions' : 'Create Supplier Special Instructions'
    const note = this.moForm.controls.supplierInstructions.value?.[0]
    const partyId = this.matchedOffer.offer.account.toString()

    const updatedNote = await this.NoteForm.showMatchedOfferNote(title, partyId, note)
    if (!updatedNote) return

    this.moForm.controls.supplierInstructions.setValue([{
      ...updatedNote,
      attributes: {
        company: [this.matchedOffer.offer.account.toString()],
        category: SPECIAL_INSTRUCTIONS,
      },
    }])
  }

  protected async addBuyerSpecialInstructions() {
    const existing = this.hasBuyerInstructions(this.moForm.getRawValue())
    const title = existing ? 'Update Buyer Special Instructions' : 'Create Buyer Special Instructions'
    const note = this.moForm.controls.buyerInstructions.value?.[0]
    const partyId = this.matchedOffer.bid.account.toString()

    const updatedNote = await this.NoteForm.showMatchedOfferNote(title, partyId, note)
    if (!updatedNote) return

    this.moForm.controls.buyerInstructions.setValue([{
      ...updatedNote,
      attributes: {
        company: [this.matchedOffer.bid.account.toString()],
        category: SPECIAL_INSTRUCTIONS,
      },
    }])
  }
}

/** statuses an MO can hold before it has gone out to the buyer */
const MATCHED_OFFER_UNSENT_STATUSES: number[] = [
  MATCHED_OFFER_INCOMPLETE,
  MATCHED_OFFER_UNSENT,
  MATCHED_OFFER_PENDING,
]

/**
 * Whether the MO is still ours to change. `message_sent_at` is the same signal the offer
 * service uses to decide when `desired_flat_price` stops floating.
 */
function isMatchedOfferUnsent(mo: DeepReadonly<MatchedOffer>) {
  return !mo.interactions?.message_sent_at && MATCHED_OFFER_UNSENT_STATUSES.includes(mo.status)
}
